import { Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ExchangeProvider } from './contexts/ExchangeContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ProtectedRoute } from './components/auth/ProtectedRoute'
import { AdminRoute } from './components/auth/AdminRoute'
import { Layout } from './components/layout/Layout'
import { AdminLayout } from './components/layout/AdminLayout'
import { RegularUserLayout } from './components/layout/RegularUserLayout'
import { HomePage } from './pages/HomePage'
import { MobileLandingPage } from './pages/MobileLandingPage'
import { MobilePreviewPage } from './pages/MobilePreviewPage'
import { LoginPage } from './pages/auth/LoginPage'
import { DashboardPage } from './pages/DashboardPage'
import { DocumentsPage } from './pages/DocumentsPage'
import { CalculatorPage } from './pages/CalculatorPage'
import { MarketplacePage } from './pages/MarketplacePage'
import { FormsPage } from './pages/FormsPage'
import { ProfilePage } from './pages/ProfilePage'
import { UserManagementPage } from './pages/UserManagementPage'
import { ActivityPage } from './pages/ActivityPage'
import { AdminDashboard } from './pages/admin/AdminDashboard'
import { AdminUsersPage } from './pages/admin/AdminUsersPage'
import { AdminExchangesPage } from './pages/admin/AdminExchangesPage'
import { AdminDocumentsPage } from './pages/admin/AdminDocumentsPage'
import { AdminMarketplacePage } from './pages/admin/AdminMarketplacePage'
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage'
import { AdminIntegrationsPage } from './pages/admin/AdminIntegrationsPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { useIsMobile } from './hooks/useIsMobile'
import { CompanyPage } from './pages/CompanyPage'
import { ContactPage } from './pages/ContactPage'
import { FAQPage } from './pages/FAQPage'
import { FeaturesPage } from './pages/FeaturesPage'
import { PricingPage } from './pages/PricingPage'
import { SecurityPage } from './pages/SecurityPage'
import { DocumentationPage } from './pages/DocumentationPage'
import { GettingStartedPage } from './pages/docs/GettingStartedPage'
import { ApiDocumentationPage } from './pages/docs/ApiDocumentationPage'
import { VideoTutorialsPage } from './pages/docs/VideoTutorialsPage'
import { ExchangeProcessPage } from './pages/docs/ExchangeProcessPage'
import { BestPracticesPage } from './pages/docs/BestPracticesPage'
import { TroubleshootingPage } from './pages/docs/TroubleshootingPage'
import { SupportPage } from './pages/SupportPage'
import { BlogPage } from './pages/BlogPage'
import { CareersPage } from './pages/CareersPage'
import { PartnersPage } from './pages/PartnersPage'
import { CompliancePage } from './pages/CompliancePage'
import { ResourcesPage } from './pages/ResourcesPage'
import { LegalPage } from './pages/LegalPage'
import { AuthModalHost } from './components/auth/AuthModalHost'
import { PublicChatLauncher } from './components/chat/PublicChatLauncher'
import { Favicon } from './components/ui/Favicon'
import { ChatProvider } from './contexts/ChatContext'
import { useAuth } from './contexts/AuthContext'
import { useLocation } from 'react-router-dom'

function AppContent() {
  const { isAuthenticated } = useAuth()
  const location = useLocation()
  const isMobile = useIsMobile()
  
  // Show public chat launcher only on public routes when not authenticated
  const isPublicRoute = !location.pathname.startsWith('/app')
  const shouldShowPublicChat = isPublicRoute && !isAuthenticated

  return (
    <div className="relative">
      <Favicon />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/mobile-preview" element={<MobilePreviewPage />} />
        <Route path="/login" element={<LoginPage />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/documentation" element={<DocumentationPage />} />
          <Route path="/documentation/getting-started" element={<GettingStartedPage />} />
          <Route path="/documentation/api" element={<ApiDocumentationPage />} />
          <Route path="/documentation/video-tutorials" element={<VideoTutorialsPage />} />
          <Route path="/documentation/exchange-process" element={<ExchangeProcessPage />} />
          <Route path="/documentation/best-practices" element={<BestPracticesPage />} />
          <Route path="/documentation/troubleshooting" element={<TroubleshootingPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/compliance" element={<CompliancePage />} />
          <Route path="/legal" element={<LegalPage />} />
        
        {/* Protected routes - Regular users (non-admin) */}
        <Route path="/app" element={
          <ProtectedRoute>
            <RegularUserLayout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="calculator" element={<CalculatorPage />} />
          <Route path="marketplace" element={<MarketplacePage />} />
          <Route path="forms" element={<FormsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="users" element={<UserManagementPage />} />
          <Route path="activity" element={<ActivityPage />} />
        </Route>
        
        {/* Admin routes - Separate layout */}
        <Route path="/app/admin" element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="exchanges" element={<AdminExchangesPage />} />
          <Route path="documents" element={<AdminDocumentsPage />} />
          <Route path="marketplace" element={<AdminMarketplacePage />} />
          <Route path="integrations" element={<AdminIntegrationsPage />} />
          <Route path="settings" element={<AdminSettingsPage />} />
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Global auth modal (desktop only, opens when not authenticated) */}
      <AuthModalHost />
      
      {/* Public chat launcher - shows on all public pages when not authenticated */}
      {shouldShowPublicChat && <PublicChatLauncher />}
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ExchangeProvider>
          <ChatProvider>
            <AppContent />
          </ChatProvider>
        </ExchangeProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

export default App
